import React, { useState } from "react";
import classnames from "classnames";
import { Whisper, WhisperProps, Tooltip as RsTooltip } from "rsuite";

import "./index.scss";

type TooltipPlacement = NonNullable<WhisperProps["placement"]>;

/** Room a one-line tooltip needs above its control: 24px tall plus rsuite's 8px offset (measured on
 *  the Users header's Filters button), with a little to spare. */
export const TOOLTIP_TOP_CLEARANCE_PX = 36;

/**
 * The side a tooltip actually opens on. rsuite's own `autoVertical` always picks whichever side has
 * more room, which would move nearly every tooltip below its control; this keeps "top" and flips to
 * "bottom" only when the control sits too close to the top of the viewport for the tooltip to fit
 * (a page header's buttons). Any other placement is returned unchanged.
 *
 * @param placement - the placement the caller asked for.
 * @param anchorTop - the control's distance from the top of the viewport, in px.
 */
export const getTooltipPlacement = (placement: TooltipPlacement, anchorTop: number): TooltipPlacement =>
  placement === "top" && anchorTop < TOOLTIP_TOP_CLEARANCE_PX ? "bottom" : placement;

export interface TooltipProps {
  /** Visible tooltip text. Purely decorative -- the wrapped control keeps its own accessible name
   *  (an `aria-label` set by the caller), so a screen reader never depends on this string.
   *
   *  Label convention (apply consistently across every caller): a control that NAVIGATES somewhere
   *  names the destination ("Back to procedures", "Back to Recognitions", "Back to settings"), never
   *  the bare "Back" when the destination is known; a control that CLOSES a panel/drawer/overlay just
   *  says "Close" (never "Close comments"/"Close drawer"/etc. -- the panel is already visually
   *  obvious, and naming it invites drift between panels' own wording); a control that performs a
   *  SINGLE ACTION names its object ("Edit goal", "Delete goal", "Delete run", "Remove work type"),
   *  never the bare verb; a toast dismiss control is "Dismiss". Sentence case throughout (capitalize
   *  only the first word and proper nouns). */
  text: string;
  /** rsuite placement keyword. Defaults to "top", which opens below instead when the control is too
   *  close to the top of the viewport (`getTooltipPlacement`); the sidebar passes "right" since the
   *  tooltip must open away from the 80px-wide rail, not over it. */
  placement?: TooltipPlacement;
  /** True when the wrapped child is itself sized by its parent (percentage width/height, or a flex
   *  child expected to fill the row) rather than by its own content -- the sidebar's `NavLink`
   *  (`display: flex`, fills the 80px rail) and every `Icon`-rendered button (`.icon` is
   *  `width/height: 100%` of whatever fixed-size box wraps it) both need this. Makes the anchor span
   *  itself `display: flex; width: 100%; height: 100%;` instead of the default `inline-flex` (which
   *  sizes to the child's own content and is correct for a self-sized control like a text button).
   *  Omitting it changes nothing for a self-sized child; passing it when unneeded is also harmless
   *  (100% of a fixed-size ancestor is that same fixed size either way). */
  block?: boolean;
  /** True while an unrelated overlay anchored to the SAME control is open (AvatarMenu's click menu on
   *  its avatar trigger) -- forces this tooltip closed instead of leaving it open from the hover that
   *  preceded the click, where it would sit on top of that overlay and intercept clicks meant for it.
   *  Omitted (or false) leaves every other caller's existing hover/focus-driven behaviour untouched. */
  forceClosed?: boolean;
  /** A single focusable element (a `<button>`, an anchor, or a component that forwards its ref to
   *  one). */
  children: React.ReactElement;
}

/**
 * The one shared hover/focus tooltip for icon-only controls. Wraps rsuite's `Whisper` + `Tooltip`,
 * whose overlay renders into a `document.body` portal so it is never clipped by an ancestor's
 * `overflow: hidden`.
 *
 * `trigger={["hover", "focus"]}` means a keyboard user tabbing onto the control sees the same text a
 * mouse user gets on hover. No `delayOpen`/`delay` here, deliberately: rsuite's `Whisper` is only
 * `enterable` when explicitly told to be, and while it isn't, `handleDelayedClose` calls `handleClose()`
 * directly WITHOUT ever clearing a pending `delayOpenTimer`
 * (`node_modules/rsuite/cjs/Overlay/OverlayTrigger.js`'s `handleDelayedOpen`/`handleDelayedClose`, both
 * gated on `if (!enterable) { return handleOpen()/handleClose(); }` -- the branch that clears the timer
 * lives below that early return and is never reached). With `delayOpen` set, sweeping the mouse across
 * several icons faster than that delay closes each one immediately on mouseout but leaves its OPEN
 * timer running -- it fires later, after the mouse has moved on, and reopens a tooltip nothing is
 * pointing at any more, with nothing left to ever close it again (measured: stuck tooltips accumulating
 * down the sidebar on a fast sweep). No delay at all means `handleOpen`/`handleClose` both call
 * `setOpen` synchronously, no timer, no race. rsuite's own ~300ms fade-OUT transition still plays before
 * a closed tooltip's node unmounts; that part was never the bug.
 *
 * `Whisper` clones whatever it wraps and overwrites that element's `ref` with its own
 * (`node_modules/rsuite/cjs/Overlay/OverlayTrigger.js`'s `cloneElement(children, { ref: triggerRef,
 * ... })` -- `ref` in that config always wins over whatever `ref` the child already had). Wrapping the
 * caller's `children` in this component's OWN `<span>` first means rsuite clones and refs THAT span,
 * never touching the real child underneath -- a ref the caller put directly on that child
 * (ChatListItem's `triggerRef`, used to return focus after Escape/Rename/Delete) survives untouched.
 * The span also carries the hover/focus listeners Whisper needs; React's focus/blur synthetic events
 * are delegated the same way click etc. are (via `focusin`/`focusout` at the root), so a child
 * receiving focus still opens the tooltip exactly as if the listener sat on the child itself.
 *
 * A `disabled` control fires no mouse events AT ALL in most browsers (not just blocking clicks), so a
 * `disabled` child could never open the tooltip on hover even though the anchor span wraps it --
 * `index.scss`'s `.app-tooltip-anchor > :disabled { pointer-events: none; }` makes the disabled
 * element invisible to hit-testing, so the hover lands on the anchor span itself instead (the standard
 * fix for "tooltip on a disabled control"). Disabled controls are still never focusable, so hover is
 * the only way to reach this text for one -- an unavoidable native-`disabled` limitation, not
 * something this component can work around.
 *
 * @param props.text - the visible tooltip text.
 * @param props.placement - rsuite placement keyword; defaults to "top".
 * @param props.block - true when the child needs the anchor to pass through its parent's box size
 *   (percentage-sized or fill-the-row children); see the prop's own doc comment.
 * @param props.forceClosed - true to force this tooltip closed regardless of hover/focus; see the
 *   prop's own doc comment.
 * @param props.children - the single focusable element to attach the tooltip to.
 */
const Tooltip = ({ text, placement = "top", block, forceClosed, children }: TooltipProps) => {
  const [resolvedPlacement, setResolvedPlacement] = useState(placement);

  // Runs in the same hover/focus event that opens the tooltip (Whisper chains these props after its own
  // open handler), so the batched render opens it on the resolved side straight away.
  const resolvePlacement = (event: React.SyntheticEvent<Element>) => {
    setResolvedPlacement(getTooltipPlacement(placement, event.currentTarget.getBoundingClientRect().top));
  };

  return (
    <Whisper
      trigger={["hover", "focus"]}
      placement={resolvedPlacement}
      speaker={<RsTooltip className="app-tooltip">{text}</RsTooltip>}
      onMouseOver={resolvePlacement}
      onFocus={resolvePlacement}
      // Passing a real boolean makes rsuite's Whisper fully controlled (closed); omitting the prop
      // (`undefined`) leaves it uncontrolled, i.e. every other caller's existing hover/focus behaviour.
      // Safe to toggle between the two across renders for this specific component: rsuite's own
      // `useControlled` (node_modules/rsuite/esm/utils/useControlled.js) re-derives "is this controlled"
      // from `open !== undefined` on EVERY render rather than latching it once, and while controlled it
      // simply ignores -- never queues -- the internal open/close calls hover/focus would otherwise
      // trigger, so nothing is lost by flipping back later.
      open={forceClosed ? false : undefined}
    >
      <span className={classnames("app-tooltip-anchor", { "app-tooltip-anchor--block": block })}>{children}</span>
    </Whisper>
  );
};

export default Tooltip;
